/*  MUI  */
import { Box, Button } from '@mui/material';
import { PageTitle } from 'application/Shared/Components/App';

/*  Shared  */
import {
    IntegrationStatusCell,
    NumberCell,
    integrationStatusFilter,
    integrationStatusFilterFn
} from 'application/Shared/Components/Cells';
import { AddClientModal } from 'application/Shared/Components/Client';
import {
    ShowPartnerTopInfoBanner,
    ShowPartnerTopInfoBannerTypes
} from 'application/Shared/Components/ShowPartnerTopInfoBanner';
import {
    Header,
    ManagedTableV2ContextProvider,
    useManagedTableV2Context
} from 'application/Shared/Components/Table';
import { Toggle } from 'application/Shared/Components/Table/types';
import { sortByIdentityIntegrationStatus } from 'application/Shared/Components/TableSortFunctions';
import { ModalFormFooter } from 'application/Shared/Modals';
import { ClientLink } from 'application/Shared/types/clientLink';

/* NPM */
import { TFunction } from 'i18next';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ClientApplicationsCell } from './internal/ClientApplicationsCell';

/* Internal */
import { ClientDevicesCell } from './internal/ClientDevicesCell';
import { ClientInfoCell } from './internal/ClientInfoCell';
import { PartnerClientsStatCards } from './internal/PartnerClientsStatCards';
import { PartnerClientsTable } from './internal/PartnerClientsTable';
import { Actions } from './internal/PartnerClientsTableActionsCell';
import {
    PartnerClientsTableContextProvider,
    usePartnerClientsTableContext
} from './internal/PartnerClientsTableContext';
import { UnclassifiedBreakdownCell } from './internal/UnclassifiedBreakdownCell';

const AddClient = () => {
    const {
        setShowAddClient,
        setTenantToEditIntegration,
        showAddClient,
        tenantToEditIntegration
    } = usePartnerClientsTableContext();
    const { setRerender } = useManagedTableV2Context();
    const [useAnIntegrationMethod, setUseAnIntegrationMethod] =
        useState('false');

    return (
        // @ts-expect-error - Component is not typed
        <AddClientModal
            formFooterComponent={
                <ModalFormFooter
                    onClose={() => {
                        setShowAddClient(false);
                        setTenantToEditIntegration(null);
                    }}
                />
            }
            handleUseAnIntegrationMethodChange={(value: string) => {
                setUseAnIntegrationMethod(value);
            }}
            onSuccess={(linkedTenantId: string | undefined) => {
                setShowAddClient(false);
                setTenantToEditIntegration(null);

                if (linkedTenantId && setRerender) {
                    setRerender(true);
                }
            }}
            show={showAddClient}
            showDescription
            skipAnimation={!!tenantToEditIntegration?.providerType}
            skipToChoosingIntegration
            tenantToEditIntegration={tenantToEditIntegration}
            useAnIntegrationMethod={
                tenantToEditIntegration ? 'true' : useAnIntegrationMethod
            }
        />
    );
};

const columns = [
    {
        accessorKey: 'tenant.name',
        cell: ClientInfoCell,
        enableColumnFilter: false,
        enableHiding: false,
        header: Header,
        id: 'clientInfo',
        name: 'Common.client',
        size: 275,
        testId: 'clientInfo',
        tooltipText: 'orglist.organizationIds'
    },
    {
        accessorKey: 'tenant.userCount',
        cell: NumberCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        header: Header,
        id: 'users',
        name: 'Common.active_users',
        size: 100,
        testId: 'activeUsers'
    },
    {
        // Throws a warning when using accessorKey: 'provider.connectionDetails'
        // https://github.com/TanStack/table/discussions/4788#discussioncomment-13120388
        accessorFn: (row: ClientLink) => row.provider,
        cell: IntegrationStatusCell,
        filterFn: integrationStatusFilterFn,
        header: Header,
        id: 'integrationStatus',
        meta: {
            filter: integrationStatusFilter
        },
        name: 'Common.identityIntegrationStatus',
        size: 150,
        sortingFn: sortByIdentityIntegrationStatus,
        testId: 'integrationStatus'
    },
    {
        accessorKey: 'devices',
        cell: ClientDevicesCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableSorting: false,
        header: Header,
        id: 'clientDevices',
        name: 'Common.devices',
        size: 100,
        testId: 'clientDevices'
    },
    {
        accessorKey: 'applications',
        cell: ClientApplicationsCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableSorting: false,
        header: Header,
        id: 'clientApplications',
        name: 'Common.applications',
        size: 200,
        testId: 'clientApplications',
        tooltipText: 'orglist.applications'
    },
    {
        accessorKey: 'unclassifiedBreakdown',
        cell: UnclassifiedBreakdownCell,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableSorting: false,
        header: Header,
        id: 'unclassifiedBreakdown',
        name: 'Common.unclassified_breakdown',
        size: 150,
        testId: 'unclassifiedBreakdown',
        tooltipText: 'orglist.unclassified_applications'
    },
    {
        accessorKey: 'actions',
        cell: Actions,
        enableColumnFilter: false,
        enableGlobalFilter: false,
        enableHiding: false,
        enableSorting: false,
        header: Header,
        id: 'actions',
        maxSize: 10,
        meta: { align: 'right', header: { justifyContent: 'flex-end' } },
        name: 'Common.actions',
        size: 10,
        testId: 'actions'
    }
];

const toggles = (t: TFunction<'translation', undefined>): Toggle[] => [
    {
        defaultEnabled: true,
        key: 'excludeHidden',
        parameter: { field: 'hidden', value: false },
        toggleText: t('Common.hide_removed_tenants')
    }
];

const PartnerClientPage = () => {
    const { t } = useTranslation();
    const { setShowAddClient, tableName, tableParameters } =
        usePartnerClientsTableContext();

    const columnPinning = {
        left: ['clientInfo'],
        right: ['actions']
    };

    return (
        <ManagedTableV2ContextProvider
            baseParameters={tableParameters}
            bulkActions={
                <>
                    <Button
                        color="primary"
                        data-trackid="addClient"
                        size="anmSmall"
                        variant="outlined"
                        onClick={() => setShowAddClient(true)}
                    >
                        {t('Common.add_client')}
                    </Button>
                    <AddClient />
                </>
            }
            columns={columns}
            getRowId="id"
            ignoreSearchParams
            initialState={{ columnPinning }}
            mode="client"
            rowsApi="/clientLink"
            rowsApiPreload
            rowsPerPage={10}
            searchEnabled
            sortDirection="DESC"
            sortKey="users"
            tableName={tableName}
            toggles={toggles(t)}
        >
            <ShowPartnerTopInfoBanner
                type={ShowPartnerTopInfoBannerTypes.SHOW_ME_HOW_TO_DEPLOY}
            />
            <PageTitle title={t('Common.clients')} />
            <PartnerClientsStatCards />
            <PartnerClientsTable />
        </ManagedTableV2ContextProvider>
    );
};

export const PartnerClientsDashboard = () => (
    <Box data-testid="partnerClientsDashboard">
        <PartnerClientsTableContextProvider>
            <PartnerClientPage />
        </PartnerClientsTableContextProvider>
    </Box>
);
